/**
 * Section heading — the H2/H3 that sits inside a <Card>, often with a
 * leading icon and a description.
 *
 *   <SectionHeading>Basic Information</SectionHeading>
 *
 *   <SectionHeading icon={<DollarSign className="w-5 h-5" />} iconColor="text-green-500">
 *     Pricing
 *   </SectionHeading>
 *
 *   <SectionHeading
 *     as="h3"
 *     description="Visible to admins only"
 *   >
 *     Advanced
 *   </SectionHeading>
 *
 * Use `as="h3"` for sub-section headings under a top-level section.
 */

import React from "react";
import { useTheme } from "../../context/ThemeContext";
import { getMutedText, getPrimaryText, getSecondaryText } from "../../utils/themeUtils";
import { TOKENS } from "../../utils/designTokens";

interface SectionHeadingProps {
  /** Heading level — h2 by default, h3 for nested sub-sections. */
  as?: "h2" | "h3";
  /** Optional icon rendered to the left of the heading text. */
  icon?: React.ReactNode;
  /**
   * Tailwind text-color class for the icon (e.g. `text-green-500`).
   * Defaults to the neutral muted text color: headings are part of the
   * neutral frame; the club's color is for navigation selection only
   * (Dan 2026-10-31).
   */
  iconColor?: string;
  /** Optional description paragraph rendered below the heading. */
  description?: React.ReactNode;
  /** Optional right-aligned content (e.g. a "View all" link). */
  actions?: React.ReactNode;
  /** Override the outer wrapper className. */
  className?: string;
  /** Children = heading text. */
  children: React.ReactNode;
}

const SectionHeading: React.FC<SectionHeadingProps> = ({
  as = "h2",
  icon,
  iconColor,
  description,
  actions,
  className = "",
  children,
}) => {
  const { isDarkMode } = useTheme();
  const Tag = as;
  const sizeClass =
    as === "h3" ? TOKENS.heading.subsection : TOKENS.heading.section;

  return (
    <div className={`${TOKENS.heading.sectionMargin} ${className}`}>
      <div className="flex items-start justify-between gap-3">
        <Tag className={`flex items-center gap-2 ${sizeClass} ${getPrimaryText(isDarkMode)}`}>
          {icon && <span className={iconColor ?? getMutedText(isDarkMode)}>{icon}</span>}
          <span>{children}</span>
        </Tag>
        {actions && <div className="flex-shrink-0">{actions}</div>}
      </div>
      {description && (
        <p className={`mt-1 text-sm ${getSecondaryText(isDarkMode)}`}>
          {description}
        </p>
      )}
    </div>
  );
};

export default SectionHeading;
